'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useSubscription } from '@/lib/hooks/use-subscription';
import { subscriptionPlanSchema } from '@/lib/subscription/types';
import { UpgradeCta } from '@/components/subscription/upgrade-cta';
import { OrbLogo } from '@/components/ui/OrbLogo';
import './pricing.css';

const checkIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5l3.5 3.5 6.5-7" />
  </svg>
);

const xIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const faqs = [
  {
    q: 'What counts as an AI question?',
    a: 'Any message you send to the AI chat, whether it\'s about a single highlighted passage, a full document, or across a topic with multiple documents. Each message counts as one question.',
  },
  {
    q: 'Can I upgrade or downgrade anytime?',
    a: 'Yes. You can upgrade to Plus at any time and your new quota kicks in immediately. If you downgrade, you keep Plus features until the end of your billing period.',
  },
  {
    q: 'What happens when I hit my question limit?',
    a: 'You can still use Docuwhy to read, highlight, and comment on PDFs. You just won\'t be able to send new AI questions until your quota resets at the start of the next month.',
  },
  {
    q: 'Which AI models are included in the free plan?',
    a: 'Free includes GPT-5.4 Mini and Gemini 3 Flash. These are fast, capable models great for most questions. Plus unlocks Claude Sonnet 4.6, GPT-5.4, and Gemini 3.1 Pro for more complex analysis.',
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="faq-list">
      {faqs.map((faq, i) => (
        <button
          key={i}
          className={`faq-item ${open === i ? 'faq-open' : ''}`}
          onClick={() => setOpen(open === i ? null : i)}
        >
          <div className="faq-q">
            <span>{faq.q}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="faq-chevron"
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          </div>
          <div className="faq-a">
            <p>{faq.a}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

export default function PricingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { data: subscription } = useSubscription({ enabled: isAuthenticated });

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setIsAuthenticated(true);
    });
  }, []);

  const userPlan = subscription?.plan;

  function renderFreeCta() {
    if (!isAuthenticated || !userPlan) {
      return <Link href="/signup" className="card-cta card-cta-free">Get Started</Link>;
    }
    if (userPlan === subscriptionPlanSchema.enum.free) {
      return <span className="card-cta card-cta-current">Current Plan</span>;
    }
    return <span className="card-cta card-cta-current">Included</span>;
  }

  function renderPlusCta() {
    if (!isAuthenticated || !userPlan) {
      return <Link href="/signup" className="card-cta card-cta-plus">Get Started</Link>;
    }
    if (userPlan === subscriptionPlanSchema.enum.plus) {
      return <span className="card-cta card-cta-current">Current Plan</span>;
    }
    return <UpgradeCta context="pricing-plus" variant="inline" className="card-cta card-cta-plus">Upgrade</UpgradeCta>;
  }

  return (
    <div className="pricing-page">
      {/* Nav */}
      <nav className={`ln-nav${scrolled ? ' ln-scrolled' : ''}`}>
        <Link href="/" className="ln-logo"><OrbLogo size={28} /> Docuwhy</Link>
        <div className="ln-nav-right">
          {isAuthenticated ? (
            <Link href="/dashboard" className="ln-nav-btn">Dashboard</Link>
          ) : (
            <>
              <Link href="/login" className="ln-nav-link">Log in</Link>
              <Link href="/signup" className="ln-nav-btn">Get Started</Link>
            </>
          )}
        </div>
      </nav>

      {/* Header */}
      <header className="pricing-header">
        <p className="pricing-label">Pricing</p>
        <h1 className="pricing-h1">Simple, transparent pricing</h1>
        <p className="pricing-sub">Start free. Upgrade when you need more.</p>
      </header>

      {/* Cards */}
      <div className="pricing-cards">
        {/* Free */}
        <div className="pricing-card">
          <div className="card-top">
            <h2 className="card-tier">Free</h2>
            <div className="card-price">
              <span className="price-amount">$0</span>
              <span className="price-period">/month</span>
            </div>
            <p className="card-desc">Get started with the basics</p>
          </div>

          <ul className="card-features">
            <li className="feat-yes">{checkIcon} 10 AI questions/month</li>
            <li className="feat-yes">{checkIcon} GPT-5.4 Mini &amp; Gemini Flash</li>
            <li className="feat-yes">{checkIcon} Highlights &amp; comments</li>
            <li className="feat-yes">{checkIcon} Topics (cross-document chat)</li>
            <li className="feat-yes">{checkIcon} 10 document uploads</li>
            <li className="feat-no">{xIcon} Premium AI models</li>
            <li className="feat-no">{xIcon} Web search in chat</li>
            <li className="feat-no">{xIcon} Thinking mode</li>
          </ul>

          {renderFreeCta()}
        </div>

        {/* Plus */}
        <div className="pricing-card card-plus">
          <div className="card-badge">Most popular</div>
          <div className="card-top">
            <h2 className="card-tier">Plus</h2>
            <div className="card-price">
              <span className="price-amount">$15</span>
              <span className="price-period">/month</span>
            </div>
            <p className="card-desc">For serious readers and researchers</p>
          </div>

          <ul className="card-features">
            <li className="feat-yes">{checkIcon} 200 AI questions/month</li>
            <li className="feat-yes">{checkIcon} All 5 AI models</li>
            <li className="feat-yes">{checkIcon} Highlights &amp; comments</li>
            <li className="feat-yes">{checkIcon} Topics (cross-document chat)</li>
            <li className="feat-yes">{checkIcon} Unlimited documents</li>
            <li className="feat-yes">{checkIcon} Claude, GPT-5.4, Gemini Pro</li>
            <li className="feat-yes">{checkIcon} Web search in chat</li>
            <li className="feat-yes">{checkIcon} Thinking mode</li>
          </ul>

          {renderPlusCta()}
        </div>
      </div>

      {/* FAQ */}
      <section className="pricing-faq">
        <h2 className="faq-h2">Questions</h2>
        <FAQ />
      </section>

      {/* Footer */}
      <footer className="ln-footer">
        <span className="ln-footer-logo"><OrbLogo size={22} /> Docuwhy</span>
        <span className="ln-footer-copy">&copy; 2026 Docuwhy</span>
      </footer>
    </div>
  );
}
